import { useState, Fragment, useEffect } from "react";
import { Badge, Button, Col, Row, Container, Card } from "react-bootstrap";
import CartInfoMonthlyDetailsModal from "components/Customize/CartInfoMonthlyDetailsModal";
import CartInfoDueTodayModal from "components/Customize/CartInfoDueTodayModal";
import CartInfoTodayInfoModal from "components/Customize/CartInfoTodayInfoModal";
import { useLocation } from "react-router-dom";
import { formatPriceString, isOneTimeChargeItem } from "utils";
import { termsAndConditionsAgreementAcceptedState, toastState } from "atoms";
import { useRecoilState, useRecoilValue } from "recoil";
import TermsAndConditionsModal from "components/Confirm/MonthlySubscriptionTermsModal";
import CustomerServiceAgreementModal from "components/Confirm/CustomerServiceAgreementModal";
import { PATH } from "utils/const";

const NOT_FOR_DISPLAY_CATEGORIES = ["Primary", "Included"];

function CartInfo({
  cart,
  onContinue,
  disableContinueButton,
  confirmVariation,
}: {
  cart: any;
  onContinue: () => void;
  disableContinueButton?: boolean;
  confirmVariation?: boolean;
}) {
  const [showMonthlyDetailsModal, setShowMonthlyDetailsModal] = useState(false);
  const [monthlyDetailsModalVariation, setMonthlyDetailsModalVariation] = useState("selected_plan");
  const [showDueTodayModal, setShowDueTodayModal] = useState(false);
  const [showInfoTodayModal, setShowInfoTodayModal] = useState(false);
  const location = useLocation();
  const tcServiceAgreement = useRecoilValue(termsAndConditionsAgreementAcceptedState);
  const [toast, setToast] = useRecoilState(toastState);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 990);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 990);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const currentCustomerConfirm = cart.isCurrentCustomer && confirmVariation;

  const cartServiceOptions = cart.list?.cartProducts
    ?.flatMap((product: any) => product.categories)
    .flatMap((category: any) => category.cartServiceOptions);

  const oneTimeCharges = cartServiceOptions.filter((serviceOption: any) => isOneTimeChargeItem(serviceOption) && serviceOption.cost > 0);

  const promotions = cartServiceOptions
    .filter((serviceOption: any) => !(serviceOption.promotionSavings === "0.00" || serviceOption.selectedQuantity === serviceOption.currentQuantity))
    .map((service: any) => ({
      key: service.promotionId,
      name: service.promotionName,
      price: service.promotionSavings,
    }));

  const setArrowPriceIndicator = (product: any, cart: any) => {
    const isSidegrade = cart?.currentServices?.monthly?.some(
      (item: any) => item.sidegrade && Object.keys(item.sidegrade).length > 0 && item.serviceCoreTypeName === "HSI"
    );

    if (product.isNew && product.serviceCoreTypeName === "HSI" && isSidegrade) {
      return <i className="me-2 text-gold fa-solid fa-arrow-down"></i>;
    }

    if (product.isNew || product.isUpgrade) {
      return <i className="me-2 text-gold fa-solid fa-arrow-up"></i>;
    }

    return null;
  };

  const renderCartProducts = (showAsUpgrade = false) => {
    return cart.list?.cartProducts?.map((product: any) => {
      const currentInternetServiceCost = cart?.currentServices?.monthly?.find(
        (product: any) => product.serviceCoreTypeName === "HSI" || product.serviceCoreTypeName === "INT"
      )?.cost;
      const categories = product.categories.reduce((a: any, category: any) => {
        a[category.name] = category;
        return a;
      }, {});
      const newInternetServiceCost = categories?.Primary?.cartServiceOptions[0]?.cost;
      //If the cost for new internet product is less than the current then it's a sidegrade.
      const isSidegrade = parseInt(newInternetServiceCost) < parseInt(currentInternetServiceCost);
      let currentProduct = cart?.currentServices?.monthly?.find((currentProduct: any) => currentProduct.productId === product.productId);
      const rows = [
        <Row key={product.id + "_product"} className="mb-2">
          <Col xs={8}>{product.productName}</Col>
          <Col xs={4} className="text-end text-nowrap">
            {setArrowPriceIndicator(product, cart)}
            {formatPriceString(categories?.Primary?.cartServiceOptions[0]?.cost)}
          </Col>
        </Row>,
      ];

      const streamTvDVRHours = () => {
        const DVRHoursText = product.categories
          .find((category: any) => category.name === "Included")
          ?.cartServiceOptions.find((option: any) => option.serviceOptionName.includes("DVR hours"))?.serviceOptionName;

        return DVRHoursText || "";
      };

      const setProductDescription = (description: string) =>
        description.includes("Stream TV Additional Stream") ? (
          <>
            <div>{description}</div>
            <div>{streamTvDVRHours()}</div>
          </>
        ) : (
          description
        );

      product.categories
        .filter((category: any) => NOT_FOR_DISPLAY_CATEGORIES.indexOf(category.name) === -1)
        .map((category: any) => category.cartServiceOptions)
        .flat()
        .filter((option: any) => option.selectedQuantity > 0)
        .filter((option: any) => !isOneTimeChargeItem(option))
        .filter((option: any) => {
          if (confirmVariation === true) {
            return option.status !== "Included";
          }
          return true;
        })
        .forEach((option: any) => {
          let currentOption = currentProduct?.cartServiceOptions?.find(
            (currentOption: any) => currentOption.serviceOptionId === option.serviceOptionId
          );
          let showOptionAsUpgrade = currentProduct == null || currentOption == null;
          if (currentOption) {
            showOptionAsUpgrade = option.selectedQuantity > currentOption.currentQuantity;
          } else if (isSidegrade) {
            showOptionAsUpgrade = option.selectedQuantity > option.currentQuantity;
          } else {
            showOptionAsUpgrade = false;
          }

          if (option.serviceOptionName !== "FCC Regulatory Fee" && option.serviceOptionName !== "Local Station Charge") {
            rows.push(
              <Row key={option.id + "_service_option"} className="mb-2">
                <Col xs={8}>{setProductDescription(option.serviceOptionName)}</Col>
                <Col xs={4} className="text-end text-nowrap">
                  {showOptionAsUpgrade && showAsUpgrade && <i className={!option.cost ? "" : "me-2 text-gold fa-solid fa-arrow-up"}></i>}
                  {option.cost !== "" ? formatPriceString(option.cost) : ""}
                </Col>
              </Row>
            );
          }
        });
      return rows;
    });
  };

  const renderPromotions = () => {
    return promotions.map((promotion: any) => (
      <Row key={promotion.key + "_promotion"} className="mb-2 customize-price-text">
        <Col xs={8}>{promotion.name}</Col>
        <Col xs={4} className="text-end">
          {formatPriceString(promotion.price.replace("-", "\u2011$"))}
        </Col>
      </Row>
    ));
  };

  const renderCartMonthlyTotal = () => {
    return (
      <Row key={"total_monthly_charges"} className="mb-2">
        <Col xs={8} className="fw-bold">
          MONTHLY TOTAL
        </Col>
        <Col xs={4} className="text-end fw-bold">
          {formatPriceString(cart.totalMonthlyCharges)}
        </Col>
      </Row>
    );
  };

  const renderDiscounts = (discounts?: any) => {
    return (
      discounts &&
      discounts.map((discount: any) => (
        <Row key={discount.name + "_promotion"} className="mb-2 customize-price-text">
          <Col xs={8}>{discount.name}</Col>
          <Col xs={4} className="text-end">
            {formatPriceString(discount.amount)}
          </Col>
        </Row>
      ))
    );
  };

  const renderOneTimeCharges = () => {
    return (
      !confirmVariation &&
      oneTimeCharges.length > 0 && (
        <Container>
          <Row className="mt-2 mb-2">
            <Col>
              <h5 className="fw-bold">One Time Charges</h5>
            </Col>
          </Row>
          {oneTimeCharges.map((serviceOption: any, index: number) => (
            <Row key={`total_monthly_charges-${index}`} className="mb-2">
              <Col xs={8}>{serviceOption.serviceOptionName}</Col>
              <Col xs={4} className="text-end">
                {formatPriceString(serviceOption.cost)}
              </Col>
            </Row>
          ))}
          <Row>
            <Col className="step-title-divider"></Col>
          </Row>
        </Container>
      )
    );
  };

  const renderDueToday = () => {
    return (
      <Container>
        {cart.dueToday?.categories && (
          <Row className="mt-2">
            <Col xs={6}>
              <h5>Due today</h5>
            </Col>
            <Col xs={6} className="text-end">
              <Button
                variant="link"
                className="text-decoration-none"
                onClick={() => {
                  setShowDueTodayModal(true);
                }}
              >
                <small>View Details</small>
              </Button>
            </Col>
          </Row>
        )}
        {cart.dueToday?.categories?.map((category: any, index: number) => {
          if (category.registerText === "Today's Total") {
            return (
              <Row key={`category-item-${index}`} className="mt-2">
                <Col xs={8} className="fw-bold">
                  {category.registerText}
                  <Badge
                    pill
                    className="ms-1 border-bg-pastel-light-blue"
                    role="button"
                    bg="primary"
                    onClick={() => {
                      setShowInfoTodayModal(true);
                    }}
                  >
                    <i className="fa-sharp fa-solid fa-info"></i>
                  </Badge>
                </Col>
                <Col xs={4} className="text-end fw-bold">
                  {formatPriceString(category.total)}
                </Col>
              </Row>
            );
          }
          return (
            <Row key={`category-item-${index}`} className="mt-2">
              <Col xs={8} style={{ textIndent: "20px" }}>
                {category.registerText}
              </Col>
              <Col xs={4} className="text-end">
                {formatPriceString(category.total)}
              </Col>
            </Row>
          );
        })}
      </Container>
    );
  };

  const renderCurrentPlan = () => {
    const divider = (
      <Row className="mb-4">
        <Col className="step-title-divider"></Col>
      </Row>
    );
    return (
      <>
        <Container>
          <Row className="mb-2">
            <Col xs={6}>
              <h5 className="fw-bold">Current Plan</h5>
            </Col>
            <Button
              variant="link"
              className="text-decoration-none pe-2 text-end col-md-6"
              onClick={() => {
                setMonthlyDetailsModalVariation("current_plan");
                setShowMonthlyDetailsModal(true);
              }}
            >
              <small>View Details</small>
            </Button>
          </Row>
        </Container>
        <Container>
          {cart.currentServices?.monthly?.map((product: any, index: number) => (
            <Fragment key={index}>
              <Row key={product.id + "_product_current"} className="mb-2">
                <Col xs={8}>{product.productName}</Col>
                <Col xs={4} className="text-end">
                  {formatPriceString(product.cost)}
                </Col>
              </Row>
              {product.cartServiceOptions.length > 0
                ? product.cartServiceOptions.map((serviceOption: any) => {
                    if (serviceOption.status === "Optional") {
                      return (
                        <Row key={serviceOption.id + "_product_current_option"} className="mb-2">
                          <Col xs={8}>{serviceOption.serviceOptionName}</Col>
                          <Col xs={4} className="text-end">
                            {formatPriceString(serviceOption.cost)}
                          </Col>
                        </Row>
                      );
                    } else {
                      return null;
                    }
                  })
                : null}
            </Fragment>
          ))}
          {renderDiscounts(cart.currentServices?.discounts)}
          {currentCustomerConfirm && divider}
          {cart.currentServices?.otherCharges && Number(cart.currentServices?.otherCharges) !== 0 && (
            <Row className="mb-2">
              <Col xs={8}>Other Charges</Col>
              <Col xs={4} className="text-end">
                {formatPriceString(cart.currentServices.otherCharges)}
              </Col>
            </Row>
          )}
          {cart.currentServices?.totalTaxesAndFees && (
            <Row className="mb-2">
              <Col xs={8}>Taxes & Fees</Col>
              <Col xs={4} className="text-end">
                {formatPriceString(cart.currentServices?.totalTaxesAndFees)}
              </Col>
            </Row>
          )}
          {cart.currentServices?.monthlyTotal && (
            <Row className="mb-2 mt-4">
              <Col xs={7} className="fw-bold">
                MONTHLY TOTAL
              </Col>
              <Col xs={5} className="text-end fw-bold">
                {formatPriceString(cart.currentServices?.monthlyTotal)}
              </Col>
            </Row>
          )}
          {!currentCustomerConfirm && divider}
        </Container>
      </>
    );
  };

  const renderNewPlan = () => {
    const currentServiceCoreTypeName: any[] = [];
    cart.list?.cartProducts?.forEach((product: any) => currentServiceCoreTypeName.push(product.serviceCoreTypeName));
    const divider = (
      <Row className="mb-4">
        <Col className="step-title-divider"></Col>
      </Row>
    );
    return (
      <>
        <Container>
          <Row className="mb-2">
            <Col xs={6}>
              <h5 className="fw-bold">New Plan</h5>
            </Col>
            <Button
              variant="link"
              className="text-decoration-none col-md-6 pe-2 text-end"
              onClick={() => {
                setMonthlyDetailsModalVariation("new_plan");
                setShowMonthlyDetailsModal(true);
              }}
            >
              <small>View Details</small>
            </Button>
          </Row>
        </Container>
        <Container>
          {cart.currentServices?.monthly
            ?.filter((service: any) => currentServiceCoreTypeName.indexOf(service.serviceCoreTypeName) === -1)
            .map((product: any) => {
              return (
                <>
                  <Row key={product.id + "_product_new"} className="mb-2">
                    <Col xs={8}>{product.productName}</Col>
                    <Col xs={4} className="text-end">
                      {formatPriceString(product.cost)}
                    </Col>
                  </Row>
                  {product.cartServiceOptions.length > 0
                    ? product.cartServiceOptions.map((serviceOption: any) => {
                        if (serviceOption.status === "Optional") {
                          return (
                            <Row key={serviceOption.id + "_product_new_option"} className="mb-2">
                              <Col xs={8}>{serviceOption.serviceOptionName}</Col>
                              <Col xs={4} className="text-end">
                                {formatPriceString(serviceOption.displayPrice)}
                              </Col>
                            </Row>
                          );
                        } else {
                          return null;
                        }
                      })
                    : null}
                </>
              );
            })}
          {renderCartProducts(true)}
          {renderPromotions()}
          {renderDiscounts(cart.discounts)}
          {currentCustomerConfirm && divider}
          {cart?.otherCharges && (
            <Row key={cart.id + "_other"} className="mb-2">
              <Col xs={8}>Other charges</Col>
              <Col xs={4} className="text-end">
                {formatPriceString(cart.otherCharges)}
              </Col>
            </Row>
          )}
          {cart?.monthlyTaxesAndFees && (
            <Row key={cart.id + "_taxes"} className="mb-4">
              <Col xs={8}>Taxes &amp; Fees</Col>
              <Col xs={4} className="text-end">
                {formatPriceString(cart.monthlyTaxesAndFees)}
              </Col>
            </Row>
          )}
          {!confirmVariation && (
            <Row key={"total_monthly_charges"} className="mb-2">
              <Col xs={8} className="fw-bold">
                MONTHLY CHARGES
              </Col>
              <Col xs={4} className="text-end fw-bold">
                {formatPriceString(cart.totalMonthlyCharges)}
              </Col>
            </Row>
          )}
          {!currentCustomerConfirm && divider}
        </Container>
      </>
    );
  };

  const renderCurrentAndNewPlan = () => {
    return (
      <>
        {renderCurrentPlan()}
        {renderNewPlan()}
      </>
    );
  };

  const renderModals = () => {
    return (
      <>
        <CartInfoMonthlyDetailsModal
          show={showMonthlyDetailsModal}
          variation={monthlyDetailsModalVariation}
          cart={cart}
          handleClose={() => {
            setShowMonthlyDetailsModal(false);
          }}
        />
        <CartInfoDueTodayModal
          show={showDueTodayModal}
          cart={cart}
          handleClose={() => {
            setShowDueTodayModal(false);
          }}
        />
        <CartInfoTodayInfoModal
          show={showInfoTodayModal}
          handleClose={() => {
            setShowInfoTodayModal(false);
          }}
        />
      </>
    );
  };

  const notifyUserToAcceptTerms = () => {
    setToast({
      ...toast,
      show: true,
      variant: "danger",
      header: "Error",
      message:
        "To continue, please review both the Monthly Subscription Terms and the Customer Service Agreement, then check the boxes in each to confirm your agreement.",
    });
  };

  if (currentCustomerConfirm) {
    return (
      <Container>
        <Row className="flex-column flex-md-row mt-4">
          <Col lg={5} sm={12}>
            <Card className="no-radius">
              <Card.Body>{renderCurrentPlan()}</Card.Body>
            </Card>
            <div className="d-block d-md-none mt-4"></div>
          </Col>
          <Col sm={1} className="confirm-arrow-container">
            <i className={`me-2 text-gold fa-solid ${isMobile ? "fa-arrow-down" : "fa-arrow-right"}`}></i>
          </Col>
          <Col lg={5} sm={12}>
            <Card className="no-radius">
              <Card.Body>
                {renderNewPlan()}
                <Container>
                  {renderCartMonthlyTotal()}
                  <Row>
                    <Col className="step-title-divider"></Col>
                  </Row>
                </Container>
                {renderDueToday()}
              </Card.Body>
              <div className={location.pathname === PATH.CONFIRM ? "agreement-buttons" : "agreement-buttons"}>
                <div className="agreement-button-containers flex-column">
                  <p className="fw-bold">
                    To continue, open and scroll to read the Monthly Subscription terms and the Customer Service Agreement then accept.
                  </p>
                  <TermsAndConditionsModal cart={cart} onContinue={onContinue} tcServiceAgreement={tcServiceAgreement} />
                  <CustomerServiceAgreementModal cart={cart} onContinue={onContinue} />
                </div>
              </div>
            </Card>
            <div>
              {(!confirmVariation || cart?.promotionCode?.enabled !== true) && (
                <div onClick={() => (disableContinueButton ? notifyUserToAcceptTerms() : onContinue())}>
                  <button className="btn btn-primary cart-registry-button" disabled={disableContinueButton}>
                    <strong>Continue</strong>
                  </button>
                </div>
              )}
            </div>
          </Col>
        </Row>
        {renderModals()}
      </Container>
    );
  }

  return (
    <Card className="no-radius">
      <Card.Body>
        {cart.isCurrentCustomer && renderCurrentAndNewPlan()}
        {!cart.isCurrentCustomer && (
          <>
            <Container>
              <Row className="mb-2">
                <Col xs={6}>
                  <h5 className="fw-bold">Selected plan</h5>
                </Col>
                <Col xs={6} className="text-end">
                  <Button
                    variant="link"
                    className="text-decoration-none"
                    onClick={() => {
                      setMonthlyDetailsModalVariation("selected_plan");
                      setShowMonthlyDetailsModal(true);
                    }}
                  >
                    <small>View Details</small>
                  </Button>
                </Col>
              </Row>
            </Container>
            <Container>
              {renderCartProducts()}
              {renderPromotions()}
              {renderDiscounts(cart.discounts)}
              {cart.promotionCode && cart.promotionCode.description && (
                <Row key={"promotion_code"} className="mb-2 customize-price-text">
                  <Col xs={8}>{cart.promotionCode.description}</Col>
                  <Col xs={4} className="text-end">
                    {formatPriceString(cart.promotionCode.discount)}
                  </Col>
                </Row>
              )}
              <Row key={cart.id + "_other"} className="mb-2">
                <Col xs={8}>Other charges</Col>
                <Col xs={4} className="text-end">
                  {formatPriceString(cart.otherCharges)}
                </Col>
              </Row>
              <Row key={cart.id + "_taxes"} className="mb-4">
                <Col xs={8}>Taxes &amp; Fees</Col>
                <Col xs={4} className="text-end">
                  {formatPriceString(cart.monthlyTaxesAndFees)}
                </Col>
              </Row>
              {!confirmVariation && renderCartMonthlyTotal()}
              <Row>
                <Col className="step-title-divider"></Col>
              </Row>
            </Container>
          </>
        )}
        {renderOneTimeCharges()}
        {renderDueToday()}
        {renderModals()}
      </Card.Body>
      {location.pathname === PATH.CONFIRM ? (
        <div className={location.pathname === PATH.CONFIRM ? "agreement-buttons" : ""}>
          <div className="text-center">
            <p className="fw-bold">
              <i className="fa-solid fa-triangle-exclamation"></i> Action Required: You must open and accept the Monthly Subscription Terms and the
              Customer Service Agreement before you can continue.
            </p>
            <div className="agreement-button-containers">
              <CustomerServiceAgreementModal cart={cart} onContinue={onContinue} />
              <TermsAndConditionsModal cart={cart} onContinue={onContinue} tcServiceAgreement={tcServiceAgreement} />
            </div>
          </div>
        </div>
      ) : null}

      <div>
        {(!confirmVariation || cart?.promotionCode?.enabled !== true) && (
          <div onClick={() => (disableContinueButton ? notifyUserToAcceptTerms() : onContinue())}>
            <button className="btn btn-primary cart-registry-button" disabled={disableContinueButton}>
              <strong>Continue</strong>
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}

export default CartInfo;
